import React, { useState } from 'react';
import {
    Box,
    Card,
    Grid,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Fade,
    Slide,
    Grow,
    IconButton,
    Chip,
    useTheme,
    alpha,
    ThemeProvider,
    createTheme,
    CssBaseline
} from '@mui/material';
import {
    Add as AddIcon,
    FolderOpen as FolderOpenIcon,
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    CloudUpload as CloudUploadIcon,
    ArrowForward as ArrowForwardIcon,
    AutoAwesome as AutoAwesomeIcon,
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ManualJDForm from './ManualJDForm';
import JDUploadMD3 from './JDUploadMD3';

// Material 3 Expressive Theme
const m3ExpressiveTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#FA370A',
            light: '#FF6B47',
            dark: '#C42D08',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#FF45C1',
            light: '#FF7AD4',
            dark: '#E63AAD',
            contrastText: '#FFFFFF',
        },
        background: {
            default: 'transparent',
            paper: 'rgba(255, 255, 255, 0.05)',
        },
        text: {
            primary: '#FFFFFF',
            secondary: 'rgba(255, 255, 255, 0.7)',
        },
    },
    typography: {
        fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
        // Display variants
        displayLarge: {
            fontSize: '3.5rem',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        displayMedium: {
            fontSize: '2.8rem',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        displaySmall: {
            fontSize: '2.2rem',
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        // Headline variants
        h1: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        // Body variants
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1.6,
            letterSpacing: '0.01em',
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
        },
        button: {
            fontWeight: 500,
            letterSpacing: '0.02em',
            textTransform: 'none',
        },
    },
    shape: {
        borderRadius: 24,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 24,
                    padding: '16px 32px',
                    fontSize: '1rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    },
                },
                contained: {
                    background: 'linear-gradient(135deg, #FA370A 0%, #FF45C1 100%)',
                    boxShadow: '0 4px 12px rgba(250, 55, 10, 0.3)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #FF4A1A 0%, #FF5AD1 100%)',
                        boxShadow: '0 6px 20px rgba(250, 55, 10, 0.4)',
                    },
                },
                outlined: {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#FFFFFF',
                    '&:hover': {
                        borderColor: '#FA370A',
                        background: 'rgba(250, 55, 10, 0.1)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 24,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                },
            },
        },
    },
});

const MotionCard = motion(Card);

const JobDescriptionStep = ({
                                manualEntryVisible,
                                setManualEntryVisible,
                                manualEntryUsed,
                                uploadUsed,
                                handleCreateManualJD,
                                manualJDData,
                                manualJDColumns,
                                handleJdFilesAdded,
                                isUploadDisabled,
                                handleListEmpty,
                                setSelectedOptions,
                                selectedOptions,
                                existingJDList,
                                jdListLoading,
                                goToNextPage,
                                backendURL,
                                handleClearManualEntry,
                                handleClearUpload
                            }) => {
    const [expandedCard, setExpandedCard] = useState(null);
    const [showNewJobOptions, setShowNewJobOptions] = useState(false);
    const theme = useTheme();

    const handleNewJobClick = () => {
        setExpandedCard('new');
        setShowNewJobOptions(true);
    };

    const handleExistingJobClick = () => {
        setExpandedCard('existing');
    };

    const handleBackToSelection = () => {
        console.log('Resetting to initial state');
        setExpandedCard(null);
        setShowNewJobOptions(false);

        // Reset all states - this is the key fix
        if (manualEntryUsed) {
            handleClearManualEntryLocal();
        }
        if (uploadUsed) {
            handleClearUploadLocal();
        }

        // Reset selected options
        setSelectedOptions(prev => ({
            ...prev,
            chosenJD: ""
        }));
    };

    const handleManualClick = () => {
        setManualEntryVisible(true);
        // Clear any uploaded files when starting manual entry
        if (uploadUsed) {
            handleClearUploadLocal();
        }
    };

    // Function to clear manual entry
    const handleClearManualEntryLocal = () => {
        console.log('Clearing manual entry');
        if (handleClearManualEntry) {
            handleClearManualEntry();
        }
    };

    // Function to clear upload
    const handleClearUploadLocal = () => {
        console.log('Clearing upload');
        if (handleClearUpload) {
            handleClearUpload();
        } else {
            handleListEmpty(); // Fallback
            handleJdFilesAdded([]); // Fallback
        }
    };

    // Function to remove manual JD
    const handleRemoveManualJD = () => {
        console.log('Removing manual JD');
        if (handleClearManualEntry) {
            handleClearManualEntry();
        }
    };

    const cardVariants = {
        initial: {
            opacity: 0,
            y: 20,
            scale: 0.95
        },
        animate: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1]
            }
        },
        hover: {
            y: -8,
            transition: {
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
            }
        },
        expanded: {
            scale: 1,
            transition: {
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1]
            }
        },
        hidden: {
            opacity: 0,
            scale: 0.8,
            x: -100,
            transition: {
                duration: 0.3,
                ease: [0.4, 0, 1, 1]
            }
        }
    };

    return (
        <ThemeProvider theme={m3ExpressiveTheme}>
            <CssBaseline />
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'transparent',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        px: { xs: 2, sm: 3, md: 4 },
                        py: { xs: 4, sm: 6, md: 8 },
                        maxWidth: '1400px',
                        mx: 'auto',
                    }}
                >
                    {/* Header Section */}
                    <Fade in={true} timeout={600}>
                        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                            <Typography
                                variant="displayLarge"
                                sx={{
                                    background: 'linear-gradient(135deg, #FFFFFF 0%, #E8E8E8 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 2,
                                    fontSize: { xs: '2.5rem', md: '3.5rem' }
                                }}
                            >
                                Let's Start With Your Job Description
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    maxWidth: '600px',
                                    mx: 'auto'
                                }}
                            >
                                {expandedCard ? 'Complete your selection below' : 'Choose how you\'d like to provide the job details'}
                            </Typography>
                        </Box>
                    </Fade>

                    {/* Back Navigation */}
                    <Slide direction="right" in={!!expandedCard} mountOnEnter unmountOnExit>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBackToSelection}
                            sx={{
                                mb: 3,
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'text.primary',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                }
                            }}
                        >
                            Back to options
                        </Button>
                    </Slide>

                    {/* Cards Container */}
                    <Grid
                        container
                        spacing={3}
                        sx={{
                            justifyContent: 'center',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {/* New Job Card */}
                        <Grid
                            item
                            xs={12}
                            md={expandedCard === 'new' ? 8 : 6}
                            sx={{
                                display: expandedCard === 'existing' ? 'none' : 'block',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            <MotionCard
                                variants={cardVariants}
                                initial="initial"
                                animate={expandedCard === 'existing' ? 'hidden' : 'animate'}
                                whileHover={!expandedCard ? 'hover' : undefined}
                                sx={{
                                    p: 4,
                                    height: '100%',
                                    minHeight: expandedCard === 'new' ? '400px' : '320px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: !expandedCard ? 'pointer' : 'default',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(135deg, rgba(250, 55, 10, 0.05) 0%, rgba(255, 69, 193, 0.05) 100%)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                    },
                                    '&:hover::before': {
                                        opacity: expandedCard ? 0 : 1,
                                    }
                                }}
                                onClick={!expandedCard && !showNewJobOptions ? handleNewJobClick : undefined}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Icon Header */}
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            background: 'linear-gradient(135deg, #FA370A 0%, #FF45C1 100%)',
                                            borderRadius: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                            boxShadow: '0 4px 12px rgba(250, 55, 10, 0.3)',
                                        }}
                                    >
                                        <AddIcon sx={{ fontSize: 32, color: 'white' }} />
                                    </Box>

                                    <Typography variant="h1" sx={{ mb: 2, color: 'text.primary' }}>
                                        New Job
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', flex: 1 }}>
                                        Create a fresh job description from scratch or upload an existing document
                                    </Typography>

                                    {/* Action Buttons */}
                                    <Box sx={{ mt: 'auto' }}>
                                        {!showNewJobOptions ? (
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                disabled={manualEntryUsed || uploadUsed || selectedOptions.chosenJD !== ""}
                                                endIcon={<ArrowForwardIcon />}
                                            >
                                                Get Started
                                            </Button>
                                        ) : (
                                            <Grow in={showNewJobOptions}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        size="large"
                                                        startIcon={<AutoAwesomeIcon />}
                                                        onClick={handleManualClick}
                                                        disabled={manualEntryUsed || uploadUsed}
                                                        sx={{
                                                            borderWidth: 2,
                                                            '&:hover': {
                                                                borderWidth: 2,
                                                            }
                                                        }}
                                                    >
                                                        Create with AI
                                                    </Button>

                                                    <JDUploadMD3
                                                        onFilesAdded={handleJdFilesAdded}
                                                        handleListEmpty={handleListEmpty}
                                                        manualEntryUsed={manualEntryUsed}
                                                        onClearManualEntry={handleClearManualEntryLocal}
                                                        disabled={manualEntryUsed}
                                                    />
                                                </Box>
                                            </Grow>
                                        )}
                                    </Box>
                                </Box>
                            </MotionCard>
                        </Grid>

                        {/* Existing Job Card */}
                        <Grid
                            item
                            xs={12}
                            md={expandedCard === 'existing' ? 8 : 6}
                            sx={{
                                display: expandedCard === 'new' ? 'none' : 'block',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            <MotionCard
                                variants={cardVariants}
                                initial="initial"
                                animate={expandedCard === 'new' ? 'hidden' : 'animate'}
                                whileHover={!expandedCard ? 'hover' : undefined}
                                sx={{
                                    p: 4,
                                    height: '100%',
                                    minHeight: expandedCard === 'existing' ? '400px' : '320px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: !expandedCard ? 'pointer' : 'default',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(135deg, rgba(250, 55, 10, 0.05) 0%, rgba(255, 69, 193, 0.05) 100%)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                    },
                                    '&:hover::before': {
                                        opacity: expandedCard ? 0 : 1,
                                    }
                                }}
                                onClick={!expandedCard ? handleExistingJobClick : undefined}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Icon Header */}
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            background: 'linear-gradient(135deg, #FA370A 0%, #FF45C1 100%)',
                                            borderRadius: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                            boxShadow: '0 4px 12px rgba(250, 55, 10, 0.3)',
                                        }}
                                    >
                                        <FolderOpenIcon sx={{ fontSize: 32, color: 'white' }} />
                                    </Box>

                                    <Typography variant="h1" sx={{ mb: 2, color: 'text.primary' }}>
                                        Existing Job
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', flex: 1 }}>
                                        Select from your previously created jobs
                                    </Typography>

                                    {/* Action Section */}
                                    <Box sx={{ mt: 'auto' }}>
                                        {!expandedCard ? (
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                disabled={manualEntryUsed || uploadUsed}
                                                endIcon={<ArrowForwardIcon />}
                                            >
                                                Browse Previous Jobs
                                            </Button>
                                        ) : (
                                            <Grow in={expandedCard === 'existing'}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel sx={{ color: 'text.secondary' }}>
                                                        Select a job description
                                                    </InputLabel>
                                                    <Select
                                                        value={selectedOptions.chosenJD || ''}
                                                        onChange={(e) => setSelectedOptions(prev => ({ ...prev, chosenJD: e.target.value }))}
                                                        label="Select a job description"
                                                        disabled={jdListLoading}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                sx: {
                                                                    backgroundColor: 'rgba(10, 10, 15, 0.95)',
                                                                    backdropFilter: 'blur(20px)',
                                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                                    borderRadius: '12px',
                                                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                                                                    maxHeight: '300px',
                                                                    '& .MuiMenuItem-root': {
                                                                        color: '#FFFFFF',
                                                                        padding: '12px 16px',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(250, 55, 10, 0.1)',
                                                                            color: '#FFFFFF',
                                                                        },
                                                                        '&.Mui-selected': {
                                                                            backgroundColor: 'rgba(250, 55, 10, 0.2)',
                                                                            color: '#FFFFFF',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(250, 55, 10, 0.3)',
                                                                            },
                                                                        },
                                                                    },
                                                                    '& .MuiList-root': {
                                                                        padding: '8px',
                                                                    },
                                                                },
                                                            },
                                                            anchorOrigin: {
                                                                vertical: 'bottom',
                                                                horizontal: 'left',
                                                            },
                                                            transformOrigin: {
                                                                vertical: 'top',
                                                                horizontal: 'left',
                                                            },
                                                        }}
                                                        sx={{
                                                            background: 'rgba(0, 0, 0, 0.2)',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.palette.primary.main,
                                                            },
                                                            '& .MuiSelect-select': {
                                                                color: '#FFFFFF',
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                color: 'rgba(255, 255, 255, 0.7)',
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                color: 'rgba(255, 255, 255, 0.7)',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>Choose a job description...</em>
                                                        </MenuItem>
                                                        {existingJDList.map((jd, index) => (
                                                            <MenuItem key={index} value={jd.JDId || ''}>
                                                                {jd.JDName || ''}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grow>
                                        )}
                                    </Box>
                                </Box>
                            </MotionCard>
                        </Grid>
                    </Grid>

                    {/* Manual JD Form Modal */}
                    <ManualJDForm
                        visible={manualEntryVisible}
                        onCreate={handleCreateManualJD}
                        onCancel={() => setManualEntryVisible(false)}
                        backendURL={backendURL}
                    />

                    {/* Manual JD Display with Remove Button and Card Styling */}
                    {manualJDData.length > 0 && (
                        <Fade in={true} timeout={800}>
                            <Box sx={{ mt: 6 }}>
                                <Card
                                    sx={{
                                        p: 4,
                                        background: 'rgba(255, 255, 255, 0.03)',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h2" sx={{ color: 'text.primary' }}>
                                            Job Details Created
                                        </Typography>
                                        <IconButton
                                            onClick={handleRemoveManualJD}
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {
                                                    color: '#FF4444',
                                                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                                }
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>

                                    {manualJDData.map((job, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                mb: 4,
                                                p: 3,
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    Job Title
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {job.jobTitle}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 3 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    Seniority
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {job.seniority}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        mb: 2,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    Job Description
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: 'text.primary',
                                                        lineHeight: 1.6,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {job.jobDescription}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Card>
                            </Box>
                        </Fade>
                    )}

                    {/* Next Button */}
                    <Fade in={true} timeout={1000}>
                        <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={goToNextPage}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    px: 6,
                                    py: 2,
                                    fontSize: '1.125rem',
                                    boxShadow: '0 8px 32px rgba(250, 55, 10, 0.3)',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 12px 48px rgba(250, 55, 10, 0.4)',
                                    }
                                }}
                            >
                                Continue to Next Step
                            </Button>
                        </Box>
                    </Fade>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default JobDescriptionStep;